/**
 * Create / edit form of one business profile. The draft holds only what the user typed:
 * null means "show the saved profile", so a refetch always shows (web/CLAUDE.md, freshness standard).
 */

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { BusinessProfile } from "../../services/naraDoor";
import {
  MAX_CREDENTIALS,
  draftErrors,
  draftFromProfile,
  draftToInput,
  emptyDraft,
  logProfileError,
  logoFileProblem,
  profileErrorKey,
  type ContactCandidate,
  type ProfileDraft,
} from "../../lib/businessProfiles";
import {
  useCreateBusinessProfile,
  useUpdateBusinessProfile,
  useUploadBusinessLogo,
} from "../../hooks/useBusinessProfiles";

const K = "businessProfiles";
const inputClass = "w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500";
const buttonClass = "inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50";

interface FormProps {
  /** Absent: a new profile. */
  profile?: BusinessProfile;
  /** The workspace has no profile yet: the first one becomes the default. */
  isFirst: boolean;
  candidates: ContactCandidate[];
  onCancel: () => void;
  onSaved: (saved: BusinessProfile, created: boolean) => void;
}

type TextField = Exclude<keyof ProfileDraft, "credentials" | "is_default">;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

function Field({ id, label, help, error, children }: { id: string; label: string; help?: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground mb-1">{label}</label>
      {children}
      {help && <p className="text-xs text-muted-foreground mt-1">{help}</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

export function BusinessProfileForm({ profile, isFirst, candidates, onCancel, onSaved }: FormProps) {
  const { t } = useTranslation();
  const create = useCreateBusinessProfile();
  const update = useUpdateBusinessProfile();
  const uploadLogo = useUploadBusinessLogo();
  const fileInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = draft ?? (profile ? draftFromProfile(profile) : { ...emptyDraft(), is_default: isFirst });
  const errors = draftErrors(current);
  const contactId = candidates.some((c) => c.userId === current.main_contact_user_id) ? current.main_contact_user_id : "";
  const defaultLocked = isFirst || !!profile?.is_default;
  const busy = create.isPending || update.isPending || uploadLogo.isPending;
  const ids = { workspaceId: profile?.workspace_id, profileId: profile?.id };

  const edit = (patch: Partial<ProfileDraft>) => setDraft({ ...current, ...patch });
  const text = (field: TextField, id: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <input id={id} className={inputClass} value={current[field]} onChange={(e) => edit({ [field]: e.target.value })} {...props} />
  );
  const fieldError = (key: keyof typeof errors) => (showErrors && errors[key] ? t(errors[key]!) : undefined);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (Object.keys(errors).length) {
      setShowErrors(true);
      return;
    }
    const input = draftToInput({ ...current, main_contact_user_id: contactId });
    try {
      if (profile) {
        const changes = current.is_default && !profile.is_default ? { ...input, is_default: true as const } : input;
        onSaved(await update.mutateAsync({ profileId: profile.id, changes }), false);
        return;
      }
      let created = await create.mutateAsync(input);
      if (current.is_default && !created.is_default) {
        created = await update.mutateAsync({ profileId: created.id, changes: { is_default: true } });
      }
      onSaved(created, true);
    } catch (err) {
      logProfileError(profile ? "updateBusinessProfile" : "createBusinessProfile", ids, err);
      setError(t(profileErrorKey(err, "save")));
    }
  };

  const pickLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !profile) return;
    setError(null);
    const problem = logoFileProblem(file);
    if (problem) {
      setError(t(problem));
      return;
    }
    try {
      await uploadLogo.mutateAsync({ profileId: profile.id, file, contentType: file.type as "image/png" | "image/jpeg" });
    } catch (err) {
      logProfileError("uploadBusinessLogo", ids, err);
      setError(t(profileErrorKey(err, "logoUpload")));
    }
  };

  const removeLogo = async () => {
    if (!profile) return;
    setError(null);
    try {
      await update.mutateAsync({ profileId: profile.id, changes: { logo_storage_key: null } });
    } catch (err) {
      logProfileError("removeBusinessLogo", ids, err);
      setError(t(profileErrorKey(err, "save")));
    }
  };

  const setCredential = (index: number, patch: Partial<{ label: string; number: string }>) =>
    edit({ credentials: current.credentials.map((c, i) => (i === index ? { ...c, ...patch } : c)) });

  return (
    <form onSubmit={save} noValidate className="mt-4 space-y-6">
      <Section title={t(`${K}.section.identity`)}>
        <Field id="bp-name" label={t(`${K}.field.name`)} help={t(`${K}.field.nameHelp`)}>{text("name", "bp-name", { maxLength: 100, autoFocus: !profile })}</Field>
        <Field id="bp-legal-name" label={t(`${K}.field.legalName`)} error={fieldError("legal_name")}>{text("legal_name", "bp-legal-name", { maxLength: 255 })}</Field>
        <Field id="bp-trade-name" label={t(`${K}.field.tradeName`)}>{text("trade_name", "bp-trade-name", { maxLength: 255 })}</Field>
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-border"
            checked={current.is_default}
            disabled={defaultLocked}
            onChange={(e) => edit({ is_default: e.target.checked })}
          />
          <span>
            <span className="block text-sm font-medium text-foreground">{t(`${K}.isDefault`)}</span>
            <span className="block text-xs text-muted-foreground">{t(`${K}.isDefaultHelp`)}</span>
          </span>
        </label>
      </Section>

      {profile && (
        <Section title={t(`${K}.section.logo`)}>
          <div className="flex items-center gap-4">
            {profile.logo_url && <img src={profile.logo_url} alt="" className="h-16 w-16 object-contain rounded border border-border bg-white" />}
            <div className="space-y-2">
              <div className="flex gap-2">
                <button type="button" className={buttonClass} disabled={busy} onClick={() => fileInput.current?.click()}>{t(`${K}.logo.upload`)}</button>
                {profile.logo_storage_key && (
                  <button type="button" className={buttonClass} disabled={busy} onClick={removeLogo}>{t(`${K}.logo.remove`)}</button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t(`${K}.logo.help`)}</p>
            </div>
            <input ref={fileInput} type="file" accept="image/png,image/jpeg" className="hidden" onChange={pickLogo} data-testid="logo-input" />
          </div>
        </Section>
      )}

      <Section title={t(`${K}.section.address`)}>
        <Field id="bp-address1" label={t(`${K}.field.addressLine1`)}>{text("address_line1", "bp-address1")}</Field>
        <Field id="bp-address2" label={t(`${K}.field.addressLine2`)}>{text("address_line2", "bp-address2")}</Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field id="bp-city" label={t(`${K}.field.city`)}>{text("city", "bp-city")}</Field>
          <Field id="bp-region" label={t(`${K}.field.region`)}>{text("region", "bp-region")}</Field>
          <Field id="bp-postal" label={t(`${K}.field.postalCode`)}>{text("postal_code", "bp-postal")}</Field>
          <Field id="bp-country" label={t(`${K}.field.country`)}>{text("country", "bp-country", { maxLength: 2 })}</Field>
        </div>
      </Section>

      <Section title={t(`${K}.section.contactInfo`)}>
        <Field id="bp-phone" label={t(`${K}.field.phone`)}>{text("phone", "bp-phone", { type: "tel" })}</Field>
        <Field id="bp-email" label={t(`${K}.field.email`)} error={fieldError("email")}>{text("email", "bp-email", { type: "email" })}</Field>
        <Field id="bp-website" label={t(`${K}.field.website`)} error={fieldError("website")}>{text("website", "bp-website")}</Field>
      </Section>

      <Section title={t(`${K}.section.mainContact`)}>
        <Field id="bp-contact" label={t(`${K}.field.contact`)} help={t(`${K}.field.contactHelp`)}>
          <select id="bp-contact" className={inputClass} value={contactId} onChange={(e) => edit({ main_contact_user_id: e.target.value })}>
            <option value="">{t(`${K}.field.contactNone`)}</option>
            {candidates.map((c) => <option key={c.userId} value={c.userId}>{c.name}</option>)}
          </select>
        </Field>
        <Field id="bp-contact-title" label={t(`${K}.field.contactTitle`)}>{text("main_contact_title", "bp-contact-title", { maxLength: 100 })}</Field>
      </Section>

      <Section title={t(`${K}.section.registration`)}>
        <Field id="bp-neq" label={t(`${K}.field.neq`)}>{text("neq", "bp-neq", { maxLength: 30 })}</Field>
        <Field id="bp-gst" label={t(`${K}.field.gst`)}>{text("gst_number", "bp-gst", { maxLength: 30 })}</Field>
        <Field id="bp-qst" label={t(`${K}.field.qst`)}>{text("qst_number", "bp-qst", { maxLength: 30 })}</Field>
      </Section>

      <Section title={t(`${K}.section.credentials`)}>
        {current.credentials.map((c, i) => (
          <div key={i} className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <Field id={`bp-cred-label-${i}`} label={t(`${K}.field.credentialLabel`)}>
              <input id={`bp-cred-label-${i}`} className={inputClass} value={c.label} onChange={(e) => setCredential(i, { label: e.target.value })} />
            </Field>
            <Field id={`bp-cred-number-${i}`} label={t(`${K}.field.credentialNumber`)}>
              <input id={`bp-cred-number-${i}`} className={inputClass} value={c.number} onChange={(e) => setCredential(i, { number: e.target.value })} />
            </Field>
            <button type="button" className={buttonClass} onClick={() => edit({ credentials: current.credentials.filter((_, j) => j !== i) })}>
              {t(`${K}.remove`)}
            </button>
          </div>
        ))}
        <button
          type="button"
          className={buttonClass}
          disabled={current.credentials.length >= MAX_CREDENTIALS}
          onClick={() => edit({ credentials: [...current.credentials, { label: "", number: "" }] })}
        >
          {t(`${K}.addCredential`)}
        </button>
      </Section>

      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2 pt-4 border-t border-border">
        <button type="button" className={buttonClass} onClick={onCancel} disabled={busy}>{t(`${K}.cancel`)}</button>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
          {t(`${K}.save`)}
        </button>
      </div>
    </form>
  );
}
